import { useState } from 'react';
import { Header, Card, Chip, Button, Badge } from '../../shared/components';
import { UsersTab } from './components/UsersTab';
import { ActivitiesTab } from './components/ActivitiesTab';
import { ThemeSwitcher } from '../../shared/theme/ThemeSwitcher';
import { useDevAuth, useCurrentUser, useSignOut } from '../../shared/auth/AuthProvider';

/**
 * Modulo: Configuración.
 * Administra las tablas de parametrizacion (Usuarios, Actividades) que alimentan
 * el resto de la plataforma, expone el cierre de sesion y el selector de usuario
 * en modo desarrollo.
 */
export function SettingsPage() {
  const [tab, setTab] = useState<'users' | 'activities'>('activities');

  return (
    <div>
      <Header title="Configuración" subtitle="Parametrización de la plataforma" />
      <div className="space-y-6 p-4">
        <SessionCard />
        <DevUserSwitcherCard />

        <div>
          <div className="mb-4 flex gap-2">
            <Chip label="Actividades" active={tab === 'activities'} onClick={() => setTab('activities')} />
            <Chip label="Usuarios" active={tab === 'users'} onClick={() => setTab('users')} />
          </div>
          {tab === 'activities' ? <ActivitiesTab /> : <UsersTab />}
        </div>
      </div>
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = { admin: 'Administrador', supervisor: 'Supervisor', operator: 'Operador' };

/** Tarjeta de sesion: quien esta conectado, su rol, el tema visual y el cierre de sesion. */
function SessionCard() {
  const user = useCurrentUser();
  const signOut = useSignOut();

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink-800">{user.fullName}</p>
          <div className="mt-1">
            <Badge tone="info">{ROLE_LABEL[user.role] ?? user.role}</Badge>
          </div>
        </div>
        {signOut && (
          <Button variant="outline" size="md" onClick={signOut}>
            Cerrar sesión
          </Button>
        )}
      </div>

      <div className="mt-4 border-t border-ink-100 pt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Tema visual</p>
        <ThemeSwitcher />
      </div>
    </Card>
  );
}

/** Solo visible en modo desarrollo (sin Clerk configurado): simula el inicio de sesión. */
function DevUserSwitcherCard() {
  let dev;
  try {
    dev = useDevAuth();
  } catch {
    return null;
  }
  return (
    <Card>
      <p className="mb-2 text-sm font-semibold text-ink-800">Sesión de desarrollo (Clerk no configurado)</p>
      <p className="mb-3 text-xs text-ink-400">
        En producción el usuario se toma automáticamente de Clerk. Mientras tanto, simula aquí con quién inicias sesión.
      </p>
      <select
        value={dev.user.id}
        onChange={(e) => dev.switchUser(e.target.value)}
        className="h-11 w-full rounded-xl border border-ink-200 px-3 text-sm"
      >
        {dev.users.map((u) => (
          <option key={u.id} value={u.id}>
            {u.fullName} — {u.role}
          </option>
        ))}
      </select>
    </Card>
  );
}
